<script setup lang="ts">
// The single modal-shell every copy-pasted dialog converges on. It wraps the
// Radix-Vue dialog primitives (Root/Portal/Overlay/Content/Title/Description) and
// paints the shared scrim + panel from SD-052 semantic tokens — nothing more.
// Focus-trap, `Esc`-to-close, outside-click and the title/description ARIA wiring
// all come from Radix; there is no hand-rolled accessibility here. It composes
// only third-party primitives (no store, router, or domain type), so it is a pure
// molecule: the ~7 dialogs' inlined `rgba(0,0,0,0.4)` scrims and panel styling die
// here and become a prop/slot swap for the migrations (tickets 09–11).
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from 'radix-vue';

withDefaults(
  defineProps<{
    /** Whether the dialog is shown; owned by the parent (v-model:open). */
    open: boolean;
    /** Accessible + visible heading. Always present so Radix can label the dialog. */
    title: string;
    /** Optional supporting line under the title, associated as the ARIA description. */
    description?: string;
    /** Panel width. `md` (28rem) matches today's default dialog; `lg` the wider ones. */
    size?: 'sm' | 'md' | 'lg';
    /**
     * Keep the title in the accessibility tree (so Radix still labels the dialog)
     * but hide it visually, for dialogs whose header is drawn in the body slot.
     */
    hideTitle?: boolean;
  }>(),
  { size: 'md', hideTitle: false, description: undefined }
);

// Every close path Radix owns — `Esc`, outside-click, a slotted `DialogClose` —
// arrives here as `update:open(false)`; the parent stays the single source of truth.
defineEmits<{
  'update:open': [boolean];
}>();
</script>

<template>
  <DialogRoot :open="open" @update:open="(v) => $emit('update:open', v)">
    <DialogPortal>
      <DialogOverlay class="dialog__overlay" />
      <DialogContent class="dialog__viewport">
        <div class="dialog__panel" :class="`dialog__panel--${size}`">
          <DialogTitle
            class="dialog__title"
            :class="{ 'dialog__title--sr-only': hideTitle }"
          >
            {{ title }}
          </DialogTitle>
          <DialogDescription v-if="description" class="dialog__description">
            {{ description }}
          </DialogDescription>
          <div class="dialog__body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="dialog__footer">
            <slot name="footer" />
          </footer>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!--
  Non-scoped by necessity: `DialogPortal` teleports the overlay + content outside
  the app tree, so Vue's scoped `data-v` hash never reaches most of this subtree
  (radix-portaled-content-needs-global-css). The `dialog__*` names are namespaced
  enough to stay global-safe, and the root theme attribute (SD-052) is what carries
  the semantic tokens into the portal. Semantic tokens only — the guardrail governs
  this block like any other.
-->
<style>
.dialog__overlay {
  position: fixed;
  inset: 0;
  background: var(--color-overlay-scrim);
  z-index: var(--z-modal-overlay);
}

.dialog__viewport {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal-content);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}

.dialog__panel {
  width: 100%;
  padding: var(--space-6);
  background: var(--color-surface-raised);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
}

.dialog__panel--sm {
  max-width: 24rem;
}

.dialog__panel--md {
  max-width: 28rem;
}

.dialog__panel--lg {
  max-width: 32rem;
}

.dialog__title {
  margin: 0 0 var(--space-2);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.dialog__title--sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.dialog__description {
  margin: 0 0 var(--space-4);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-6);
}
</style>
